import { Injectable, Injector } from '@angular/core';
import { WidgetsContainerAbstract } from 'src/app/abstract/widgets/widgets-container.abstract';
import { WidgetsAbstract } from 'src/app/abstract/widgets/widgets.abstract';
import { WidgetsTypeEnum } from 'src/app/enums/widgets/widgets-type.enum';
import { WidgetsFormModel } from 'src/app/models/widgets/widgets-form.model';
import { Logger } from 'src/app/core/logger';
import { WidgetsSectionModel } from 'src/app/models/widgets/widgets-section.model';
import { ConditionalPrimitive } from 'src/app/models/formly/conditional-primitive.model';
import { Config } from 'src/app/config';
import { BehaviorSubject } from 'rxjs';
import { WidgetsResequenceService } from './widgets-resequence.service';
import { WidgetsTreeExpandService } from './widgets-tree-expand.service';
import { WidgetsDragAndDropService } from './widgets-drag-drop.service';
import { WidgetsDatapointsService } from './widgets-datapoints.service';
import { WidgetsNodeService } from './widgets-node.service';
@Injectable({
  providedIn: 'root',
})
export class WidgetsService {
  // Globally store the root of the formWidget Tree in the service
  /* eslint-disable */
  private readonly _layoutRoot = new BehaviorSubject<WidgetsFormModel>(WidgetsService.createEmptyRootForm());
  public readonly layoutRoot$ = this._layoutRoot.asObservable();

  // Used to check if layout has been modified. Pristine = untouched = unmodified
  public isPristineActive: boolean = true;
  private pristine: boolean = true;

  // WidgetsService will evolve to be a Facade service for all layout widget tree operations
  private _nodeService?: WidgetsNodeService;
  private _resequenceService?: WidgetsResequenceService;
  private _expandService?: WidgetsTreeExpandService;  
  private _dragAndDropService?: WidgetsDragAndDropService;
  private _datapointsService?: WidgetsDatapointsService;  
  /* eslint-enable */

  constructor(
    private injector: Injector,
    private logger: Logger) {
    // Every time a new layout root is set we need to rebuild the droplist id list for drag & drop
    this.layoutRoot$.subscribe((newLayoutRoot) => {
      this.logger.log("LayoutRoot "+newLayoutRoot.name+" loaded");
      this.dragAndDropService.rebuildDropListIds(newLayoutRoot);
    });

    // Create an initial empty form with default data and selected state
    // this.layoutRoot = this.createEmptyRootForm();

    // For now, layout is pristine = untouched = unmodified
    this.resetPristine();
  }

  // Node Tree operations service
  public get nodeService(): WidgetsNodeService {
    if(!this._nodeService){
      this._nodeService = this.injector.get(WidgetsNodeService);
    }
    return this._nodeService;
  }

  // Resequence question and sections service
  public get resequenceService(): WidgetsResequenceService {
    if(!this._resequenceService){
      this._resequenceService = this.injector.get(WidgetsResequenceService);
    }
    return this._resequenceService;
  }

  // Expand / Collapse tree service
  public get expandService(): WidgetsTreeExpandService {
    if(!this._expandService){
      this._expandService = this.injector.get(WidgetsTreeExpandService);
    }
    return this._expandService;
  }

  // Drag / Drop service
  public get dragAndDropService(): WidgetsDragAndDropService {
    if(!this._dragAndDropService){
      this._dragAndDropService = this.injector.get(WidgetsDragAndDropService);
    }
    return this._dragAndDropService;
  }

  // Datapoints service
  public get datapointsService(): WidgetsDatapointsService {
    if(!this._datapointsService){
      this._datapointsService = this.injector.get(WidgetsDatapointsService);
    }
    return this._datapointsService;
  }

  /**
   * Getter for root Layout
   *
   * @returns Current value of the WidgetsFormModel layout
   */
  get layoutRoot(): WidgetsFormModel {
    return this._layoutRoot.getValue();
  }

  /**
   * Setter for root Layout
   *
   * @param form New Form to be the root layout will be
   */
  set layoutRoot(form: WidgetsFormModel) {
    this._layoutRoot.next(form);
  }

  public onSectionExpandAll() {
      const expandContent: any = document.querySelectorAll('.expand-more');
      expandContent.forEach(element => {
        element.click();
      });
  }
  
  public onSectionCollapseAll() {
      const expandContent: any = document.querySelectorAll('.expand-less');
      expandContent.forEach(element => {
        element.click();
      });
  }

  /**
   *
   * @param widget
   * @param widgetParent
   * @returns true if widgetParent is parent of widget
   */
  public static isParentOfWidget(
    widgetParent: WidgetsAbstract,
    widget: WidgetsAbstract
  ): boolean {
    let model;
    switch (widgetParent.widgetType) {
      case WidgetsTypeEnum.FORM:
      case WidgetsTypeEnum.ROW:
      case WidgetsTypeEnum.COLUMN:
      case WidgetsTypeEnum.ARRAY:
      case WidgetsTypeEnum.SECTION:
        {
          model = widgetParent as WidgetsContainerAbstract;
          model.children.forEach((childWidget) => {
            WidgetsService.isParentOfWidget(widget, childWidget);
          });
        }
        break;
      case WidgetsTypeEnum.FIELD:
      case WidgetsTypeEnum.TEXT:
      case WidgetsTypeEnum.RADIO:
      case WidgetsTypeEnum.TIME_RANGE:
      case WidgetsTypeEnum.CHECKBOX:
      case WidgetsTypeEnum.INPUT:
      case WidgetsTypeEnum.ORDINAL_IN_SET:
        if (
          widget.id === widgetParent.id &&
          widget.widgetType === widgetParent.widgetType
        )
          {return true;}
    }

    return false;
  }

  /**
   * The initial form is created with default data and selected state
   */
  static createEmptyRootForm(): WidgetsFormModel {
    const form = new WidgetsFormModel();
    form.isSelected = true;
    form.createdBy = Config.user.iNumber;
    form.lastUpdated = new Date();
    form.created = form.lastUpdated.toISOString();
    return form;
  }

  /**
   * Return the current Layout root tree
   *
   * @returns
   */
   public getLayoutRoot(): WidgetsFormModel {
    return this.layoutRoot;
  }

  /**
   * Set the specified WidgetFormModel as the Layout tree root
   *
   * @param layoutRoot
   */
  public setLayoutRoot(newLayoutRoot: WidgetsFormModel) {
    // Let a subscription do this...
    // this.rebuildDropListIds(newLayoutRoot);

    // New layout is replaced
    this.layoutRoot = newLayoutRoot;
  }

  public getLastUpdated(): Date | undefined {
    return this.layoutRoot.lastUpdated;
  }

  public setLastUpdated(lastUpdate: Date) {
    this.layoutRoot.lastUpdated = lastUpdate;
    // const prevRoot = this.layoutRoot;
    // prevRoot.lastUpdated = lastUpdate;
    // this.layoutRoot = prevRoot;
  }

  /**
   * Check if layout is Pristine = untouched = unmodified
   *
   * @returns true if it has not been modified
   */
  public isPristine(): boolean {
    return this.pristine;
  }

  public setLayoutTouched() {
    this.pristine = false;
  }

  public resetPristine() {
    this.pristine = true;
  }

  /**
   * Retrieve Widget Node with specific uuid
   *
   * @deprecated Use getNodeAsync instead
   * @param widgetParent
   * @param uuid of the node to find
   * @returns node found
   */
  public getNode(
    widgetParent: WidgetsAbstract,
    uuid: string
  ): WidgetsAbstract | undefined {
    return this.nodeService.getNode(widgetParent, uuid);
  }

  /**
   * Retrieve Widget Node with specific uuid
   *
   * @param widgetParent
   * @param uuid
   * @returns Promise of found node
   */
  public async getNodeAsync(
    widgetParent: WidgetsAbstract,
    uuid: string
  ): Promise<WidgetsAbstract | undefined> {
    return this.nodeService.getNodeAsync(widgetParent, uuid);
  }

  /**
   * Update specified node in the tree.
   * NOTE: If the parent node is the node to be updated it will not work
   *
   * @param widget
   * @param searchedNode
   * @returns Promise of node replaced
   */
  public async updateNodeAsync(
    widget: WidgetsAbstract,
    searchedNode: WidgetsAbstract
  ): Promise<WidgetsAbstract|undefined> {
    return this.nodeService.updateNodeAsync(widget, searchedNode);
  }

  /**
   *
   * @param type Field type (input, checkbox, select)
   * @returns Array with the keys for all fields of a specified type
   */
  public getAllConditionalFields(): ConditionalPrimitive[] {
    return this.nodeService.getAllConditionalFields();
  }

  /**
   *
   * @param type Field type (input, checkbox, select)
   * @returns Array with the keys for all fields of a specified type
   */
  public getConditionalFieldsInNode(
    nodeId: string | undefined
  ): ConditionalPrimitive[] {
    return this.nodeService.getConditionalFieldsInNode(nodeId);
  }

  /**
   *
   * @param type Field type (input, checkbox, select)
   * @returns Array with the keys for all fields of a specified type
   */
  public getAllFieldsOfType(type: string): string[] {
    return this.nodeService.getAllFieldsOfType(type);
  }

  public getAllFieldsOfLabel(type: string, label: string): string[] {
    return this.nodeService.getAllFieldsOfLabel(type, label);
  }

  /**
   * Checks all fields of the same type and according to the number
   * returns a valid name for the next field of the same type
   *
   * @example
   * For 'radio' as input, 'radio3' is returned,
   * since there are 3 other fields of that type
   * @param type Example: radio
   * @returns Suggested name for the next key. Example: radio3
   */
  public getNextKey(type: string) {
    return this.nodeService.getNextKey(type);
  }

  /**
   * @example
   * For a field name 'Fire Extinguishers Available' it returns fireExtinguishersAvailable2 for a new key.
   * @param name
   * @returns The name of the widget in lowercase and with '-' instead of spaces + index number
   */
  public getNextKeyForName(name: string): string {
    return this.nodeService.getNextKeyForName(name);
  }

  /**
   * Only useful when adding a new section
   *
   * @param level
   * @param parentSeq sequence number of the parent (0 if TOP level sections are searched)
   * @returns
   */
  public getNextSection(parent: WidgetsContainerAbstract): number {
    const sequences: Array<string> = [];

    parent.children.forEach((childWidget) => {
      if (childWidget.widgetType === WidgetsTypeEnum.SECTION) {
        const section = childWidget as WidgetsSectionModel;
        sequences.push(section.label);
      }
    });
    return sequences.length + 1;
  }

  /**
   * Traverse widget tree checking for sections and updating the sequence hierarchy.
   * Note: uses a class private variable sectionSequence
   *
   * @param widget
   * @param parent
   * @returns Number of Sections
   */
   public async resequenceSections(
    widget: WidgetsAbstract,
    parent: WidgetsContainerAbstract
    ): Promise<number> {
      return this.resequenceService.resequenceSections(widget, parent);
    }

  /**
   * Traverse widget tree checking for questions and updating the question sequence number.
   * Note: uses a class private variable questionSequence
   *
   * @param widget
   * @param widget
   * @returns true if widgetParent is parent of widget
   */
  public async resequenceQuestions(widget: WidgetsAbstract): Promise<number> {
    return this.resequenceService.resequenceQuestions(widget);
  }

  /**
   * delete one node from tree
   *
   * @param data WidgetsAbstract to delete
   */
  async deleteNode(data: WidgetsAbstract, parent?:WidgetsAbstract): Promise<boolean> {
    return this.nodeService.deleteNode(data, parent);
  }

  public async getSelectedNode(): Promise<WidgetsAbstract | undefined> {
    return this.nodeService.getSelectedNode();
  }

  /**
   * This method traverses the layout tree and each time it finds
   * a node with a key, it adds it to the keyList.
   * Exceptions:
   * - not for questions
   * - not for sections
   *
   * @param keyList
   * @param widget
   * @returns
   */
  public async getDataPointsList(
    keyList: string[],
    widget: WidgetsAbstract
  ): Promise<void> {
    return this.datapointsService.getDataPointsList(keyList, widget, 0);
  }

  /**
   * Expand all sections
   *
   * @returns
   */
  public async expandAll(): Promise<void> {
    return this.expandService.expandAll();
  }

  /**
   * Collapse all sections
   *
   * @param expand
   * @returns
   */
   public async collapseAll(): Promise<void> {
    return this.expandService.collapseAll();
  }

  /**
   * Get (Drag and) Dropped ids
   *
   * @returns
   */
  public getDroppedIds(): string[] {
    return this.dragAndDropService.getDroppedIds();
  }

  public setDroppedIds(idList: string[]) {
    this.dragAndDropService.setDroppedIds(idList);
  }

  /**
   *
   * @param type
   * @returns Next sequential number for the key of a field to be used in Formly
   * for customization purposes
   */
   private getNextFieldsSeqForType(type: string): number {
    // TODO: Traverse widget tree and check for this input type (input, checkbox, select) what's the max and autoincrement
    // FIXME: For now: Between 0 and max
    const random = Math.floor(Math.random() * (200 + 1));
    return random;
  }
}
